import React, { useEffect } from 'react';
import LustLinkSeal from '../components/common/LustLinkSeal';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Lock, Video, ArrowRight, Star, Broadcast } from '@phosphor-icons/react';

const HERO_BG = 'https://images.unsplash.com/photo-1765657636229-a829542aeea8?w=1920&q=80&auto=format';
const CREATOR_IMG_1 = 'https://images.unsplash.com/photo-1770290924629-5c74da19051c?w=800&q=80&auto=format';
const CREATOR_IMG_2 = 'https://images.unsplash.com/photo-1653059972552-1a216785f842?w=800&q=80&auto=format';
const GALLERY_1 = 'https://images.unsplash.com/photo-1774532969543-d2872d588943?w=600&q=80&auto=format';
const GALLERY_2 = 'https://images.unsplash.com/photo-1584622244945-ae5b042565aa?w=600&q=80&auto=format';
const GALLERY_3 = 'https://images.unsplash.com/photo-1635760038935-825a266534da?w=600&q=80&auto=format';
const GALLERY_4 = 'https://images.unsplash.com/photo-1565090474977-b4eefefd0c90?w=600&q=80&auto=format';

const LandingPage = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) sessionStorage.setItem('referral_code', ref);
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-[#09090b] overflow-hidden">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${HERO_BG}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/80 to-[#09090b]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-[#09090b]/60" />
        
        <div className="relative z-10 container mx-auto px-6">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center gap-3 mb-8">
                <LustLinkSeal size={48} />
                <h1 className="font-heading text-3xl font-black tracking-tight text-white">
                  Lust<span className="text-blue-500">Link</span>
                </h1>
              </div>
              
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter text-white mb-6 leading-[0.95]">
                Desire Has<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">
                  A New Home
                </span>
              </h2>
              
              <p className="text-slate-400 italic text-sm mb-4">
                A platform for content creators, built by a content creator.
              </p>

              <p className="text-slate-300 text-lg sm:text-xl max-w-lg mb-8 leading-relaxed">
                Free adult content and live webcam performances. 
                Uncensored. Empowering. Yours.
              </p>
              
              <div className="flex flex-col sm:flex-row items-start gap-4 mb-12">
                <Link
                  to="/auth"
                  data-testid="get-started-btn"
                  className="group px-8 py-4 bg-blue-500 text-white rounded-full font-semibold text-lg hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/25 flex items-center gap-2"
                >
                  Get Started Free
                  <ArrowRight weight="bold" className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/auth?mode=login"
                  data-testid="sign-in-btn"
                  className="px-8 py-4 border border-white/20 text-white rounded-full font-medium text-lg hover:bg-white/10 backdrop-blur-sm transition-colors"
                >
                  Sign In
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Provocative Gallery Strip */}
      <section className="py-2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090b] via-transparent to-[#09090b] z-10" />
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex gap-2 justify-center"
        >
          {[GALLERY_1, GALLERY_2, GALLERY_3, GALLERY_4].map((src, i) => (
            <div key={i} className="relative w-1/4 max-w-[300px] aspect-[3/4] overflow-hidden rounded-xl">
              <img 
                src={src} 
                alt="" 
                className="w-full h-full object-cover opacity-60 hover:opacity-90 transition-opacity duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          ))}
        </motion.div>
      </section>

      {/* Features */}
      <section className="py-20 relative">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h3 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You <span className="text-blue-500">Crave</span>
            </h3>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">
              One platform, endless possibilities for creators and fans alike.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { icon: Heart, label: 'Free Content', desc: 'Browse thousands of photos and videos from top creators — completely free', color: 'rose' },
              { icon: Broadcast, label: 'Live Webcams', desc: 'Watch or go live — real-time streaming with interactive chat', color: 'amber', badge: 'Live' },
              { icon: Lock, label: 'Privacy First', desc: 'Your data, your rules. Always encrypted, always secure.', color: 'emerald' },
            ].map((feature, i) => (
              <motion.div
                key={feature.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative group"
              >
                <div className="rounded-2xl bg-slate-900/80 border border-white/5 p-6 h-full hover:border-blue-500/20 transition-all hover:bg-slate-900">
                  {feature.badge && (
                    <span className="absolute -top-2 -right-2 px-2.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                      {feature.badge}
                    </span>
                  )}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    feature.color === 'rose' ? 'bg-blue-500/15 text-blue-400' :
                    feature.color === 'amber' ? 'bg-amber-500/15 text-amber-400' :
                    feature.color === 'purple' ? 'bg-indigo-500/15 text-indigo-400' :
                    'bg-emerald-500/15 text-emerald-400'
                  }`}>
                    <feature.icon weight="duotone" className="w-6 h-6" />
                  </div>
                  <h4 className="text-white font-semibold mb-2">{feature.label}</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Creator Section with Provocative Imagery */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 mb-4 block">
                For Creators
              </span>
              <h3 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
                Monetize Your<br />
                <span className="text-blue-500">Passion</span>
              </h3>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Set your own subscription tiers, sell exclusive content with tokens, 
                and build a devoted audience. Keep 80% of everything you earn.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="rounded-xl bg-slate-900/80 border border-white/5 px-5 py-4">
                  <div className="text-2xl font-bold text-blue-500">80%</div>
                  <div className="text-sm text-slate-400">Revenue Share</div>
                </div>
                <div className="rounded-xl bg-slate-900/80 border border-white/5 px-5 py-4">
                  <div className="text-2xl font-bold text-amber-400">$0</div>
                  <div className="text-sm text-slate-400">Platform Fee</div>
                </div>
                <div className="rounded-xl bg-slate-900/80 border border-white/5 px-5 py-4">
                  <div className="text-2xl font-bold text-white flex items-center gap-1">
                    <Star weight="fill" className="w-5 h-5 text-amber-400" />
                    4.9
                  </div>
                  <div className="text-sm text-slate-400">Creator Rating</div>
                </div>
              </div>
              <Link
                to="/auth?role=creator"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/25"
              >
                Start Creating
                <ArrowRight weight="bold" className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden">
                  <img 
                    src={CREATOR_IMG_1}
                    alt="Creator"
                    className="w-full h-72 object-cover hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden mt-8">
                  <img 
                    src={CREATOR_IMG_2}
                    alt="Creator"
                    className="w-full h-72 object-cover hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="absolute -bottom-4 left-4 right-4 rounded-2xl bg-slate-900/90 backdrop-blur-lg border border-white/10 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-semibold">Start creating today</div>
                    <div className="text-sm text-slate-400">No upfront costs</div>
                  </div>
                  <Link
                    to="/auth?role=creator"
                    className="px-4 py-2 bg-blue-500 text-white rounded-full text-sm font-semibold hover:bg-blue-600 transition-colors"
                  >
                    Join Now
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="border-t border-white/5 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <LustLinkSeal size={24} showArcText={false} />
              <span className="font-heading font-bold text-white">LustLink</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500 flex-wrap justify-center">
              <span>18+ Only</span>
              <a href="/legal/content-policy" className="hover:text-slate-300 transition-colors">Content Policy</a>
              <a href="/legal/terms" className="hover:text-slate-300 transition-colors">Terms of Service</a>
              <a href="/legal/2257" className="hover:text-slate-300 transition-colors">2257 Compliance</a>
              <a href="/legal/dmca" className="hover:text-slate-300 transition-colors">DMCA</a>
              <span>&copy; 2026 Ganso Gordo Producciones LLC</span>
              <span className="text-slate-700 text-xs">v4.8.4</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
